import React, { useEffect, useMemo, useState } from 'react';
import type { ParagraphBlock, LegalRun } from '../../EdgeDocsCore/models/ast';
import { layoutParagraph } from '../../EdgeDocsCore/engine/ParagraphLayout';
import { getStylesRegistry, setStylesRegistry } from '../../EdgeDocsCore/styles/registry';
import { getFontMetrics } from '../../EdgeDocsCore/measurement/FontMetrics';
import { resolveFont } from '../../EdgeDocsCore/measurement/FontResolver';

function segmentG(text: string): string[] {
  const anyIntl: any = Intl as any;
  if (anyIntl?.Segmenter) {
    const seg = new anyIntl.Segmenter(undefined, { granularity: 'grapheme' });
    return Array.from(seg.segment(text)).map((s: any) => s.segment);
  }
  return Array.from(text);
}

function TypesetPreview({
  text,
  width,
  lineBoxes,
  graphemeMap,
  runId,
  fontFamily,
  fontSizePx,
  fontWeight,
}: {
  text: string;
  width: number;
  lineBoxes: any[];
  graphemeMap: Record<string, { lineIndex: number; x: number }>;
  runId: string;
  fontFamily: string;
  fontSizePx: number;
  fontWeight: number;
}) {
  const style = useMemo<React.CSSProperties>(() => ({ position: 'relative', width: `${width}px`, border: '1px solid #e5e7eb', padding: 8, background: '#fff' }), [width]);
  const graphemes = useMemo(() => segmentG(text), [text]);
  const ascent = useMemo(() => {
    const pxToPt = (px: number) => px * (72 / 96);
    const resolved = resolveFont({ fontFamily, fontSizePt: Math.max(1, Math.round(pxToPt(fontSizePx))), fontWeight });
    const metrics = getFontMetrics(resolved);
    return Math.round(metrics.ascentPx);
  }, [fontFamily, fontSizePx, fontWeight]);
  return (
    <div style={style}>
      {/* faint native rendering */}
      <div style={{ opacity: 0.2, pointerEvents: 'none', fontFamily, fontSize: `${fontSizePx}px` }}>{text}</div>
      {graphemes.map((g, i) => {
        const key = `${runId}:${i}`;
        const pos = graphemeMap[key];
        if (!pos) return null;
        const line = lineBoxes[pos.lineIndex];
        if (!line) return null;
        return (
          <span
            key={`glyph-${i}`}
            style={{
              position: 'absolute',
              left: pos.x,
              top: line.baseline - ascent,
              fontFamily,
              fontSize: `${fontSizePx}px`,
              lineHeight: 1,
              pointerEvents: 'none',
              whiteSpace: 'pre',
            }}
          >
            {g}
          </span>
        );
      })}
    </div>
  );
}

function useParagraphLayout(
  text: string,
  widthPx: number,
  fontFamily: string,
  fontSizePt: number,
  fontWeight: number,
  lineHeight: number,
) {
  const [heightPx, setHeightPx] = useState(0);
  const [lineBoxes, setLineBoxes] = useState<Array<{
    x: number; y: number; width: number; height: number; baseline: number;
    lineIndexInBlock: number; startGrapheme: number; endGrapheme: number;
  }>>([]);
  const [graphemeMap, setGraphemeMap] = useState<Record<string, { lineIndex: number; x: number }>>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Install a temporary registry override for devtools
        const base = getStylesRegistry();
        const next = {
          characterStyles: { ...(base.characterStyles || {}) },
          paragraphStyles: { ...(base.paragraphStyles || {}) },
        } as any;
        next.characterStyles['devtools_char'] = {
          id: 'devtools_char',
          fontFamily,
          fontSizePt,
          fontWeight,
        };
        next.paragraphStyles['devtools_para'] = {
          id: 'devtools_para',
          lineSpacingMultiple: lineHeight,
          defaultCharacterStyleId: 'devtools_char',
        };
        setStylesRegistry(next);

        const runs: LegalRun[] = [{ id: 'r0', text, marks: {} }];
        const block: ParagraphBlock = { id: 'p1', type: 'Paragraph', attrs: {}, children: runs, styleId: 'devtools_para' } as any;
        const res = await layoutParagraph(block, { widthPx });
        if (!cancelled) {
          setLineBoxes(res.lineBoxes);
          setHeightPx(res.heightPx);
          setGraphemeMap(res.graphemeMap);
          setError(null);
        }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Measurement failed');
      }
    })();
    return () => { cancelled = true; };
  }, [text, widthPx, fontFamily, fontSizePt, fontWeight, lineHeight]);

  return { heightPx, lineBoxes, graphemeMap, error };
}

export function ParagraphInspector(): React.JSX.Element {
  const [text, setText] = useState<string>('The quick brown fox jumps over the lazy dog.');
  const [width, setWidth] = useState<number>(320);
  const [fontFamily, setFontFamily] = useState<string>('Inter');
  const [fontSizePt, setFontSizePt] = useState<number>(12);
  const [fontWeight, setFontWeight] = useState<number>(400);
  const [lineHeight, setLineHeight] = useState<number>(1.5);
  const { heightPx, lineBoxes, graphemeMap, error } = useParagraphLayout(
    text, width, fontFamily, fontSizePt, fontWeight, lineHeight
  );
  const [hoverKey, setHoverKey] = useState<string | null>(null);
  const [showMarkers, setShowMarkers] = useState<boolean>(true);

  const previewStyle = useMemo<React.CSSProperties>(() => ({
    position: 'relative',
    width: `${width}px`,
    border: '1px solid #e5e7eb',
    padding: '8px',
    fontFamily: `${fontFamily}, system-ui, Arial, sans-serif`,
    fontSize: `${Math.round(fontSizePt * (96 / 72))}px`,
    lineHeight: lineHeight,
    background: '#fff',
  }), [width, fontFamily, fontSizePt, lineHeight]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16 }}>
      <div>
        <h3 style={{ margin: '8px 0' }}>Controls</h3>
        <label style={{ display: 'block', marginBottom: 8 }}>
          Width: {width}px
          <input
            type="range"
            min={180}
            max={640}
            value={width}
            onChange={(e) => setWidth(Number(e.target.value))}
            style={{ width: '100%' }}
          />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <label>
            Font Family
            <select value={fontFamily} onChange={(e) => setFontFamily(e.target.value)} style={{ width: '100%' }}>
              <option value="Inter">Inter</option>
              <option value="Arial">Arial</option>
              <option value="Helvetica">Helvetica</option>
              <option value="Times New Roman">Times New Roman</option>
              <option value="Georgia">Georgia</option>
              <option value="Courier New">Courier New</option>
              <option value="Noto Sans">Noto Sans</option>
              <option value="Noto Serif">Noto Serif</option>
            </select>
          </label>
          <label>
            Font Size (pt)
            <input type="number" value={fontSizePt} onChange={(e) => setFontSizePt(Number(e.target.value))} style={{ width: '100%' }} />
          </label>
          <label>
            Weight
            <input type="number" value={fontWeight} onChange={(e) => setFontWeight(Number(e.target.value))} style={{ width: '100%' }} />
          </label>
          <label>
            Line Height
            <input type="number" step="0.1" value={lineHeight} onChange={(e) => setLineHeight(Number(e.target.value))} style={{ width: '100%' }} />
          </label>
        </div>
        {error && <div style={{ color: '#dc2626', fontSize: 12, marginTop: 8 }}>Error: {error}</div>}
        <label style={{ display: 'block', marginTop: 8 }}>
          <input type="checkbox" checked={showMarkers} onChange={(e) => setShowMarkers(e.target.checked)} /> Show grapheme markers
        </label>
        <label style={{ display: 'block' }}>
          Text
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            style={{ width: '100%', fontFamily: 'inherit' }}
          />
        </label>
        <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>
          DPR: {typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 'n/a'} | Lines: {lineBoxes.length} | Height: {Math.round(heightPx)}px
        </div>
      </div>

      <div>
        <h3 style={{ margin: '8px 0' }}>Preview</h3>
        <div style={previewStyle}>
          <div>{text}</div>
          {/* Overlays */}
          {lineBoxes.map((b, i) => (
            <div key={`box-${i}`} style={{
              position: 'absolute',
              left: b.x,
              top: b.y,
              width: b.width,
              height: b.height,
              border: '1px dashed rgba(37,99,235,0.6)',
              boxSizing: 'border-box',
              pointerEvents: 'none',
            }} />
          ))}
          {lineBoxes.map((b, i) => (
            <div key={`baseline-${i}`} style={{
              position: 'absolute',
              left: 0,
              top: b.baseline,
              width: '100%',
              height: 0,
              borderTop: '1px solid rgba(250,204,21,0.7)',
              pointerEvents: 'none',
            }} />
          ))}
          {/* Grapheme markers */}
          {showMarkers && Object.entries(graphemeMap).map(([key, val]) => {
            const line = lineBoxes[val.lineIndex];
            if (!line) return null;
            const isActive = hoverKey === key;
            return (
              <div
                key={`gm-${key}`}
                title={key}
                onMouseEnter={() => setHoverKey(key)}
                onMouseLeave={() => setHoverKey(null)}
                style={{
                  position: 'absolute',
                  left: val.x,
                  top: line.baseline - 6,
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: isActive ? 'rgba(220,38,38,0.9)' : 'rgba(99,102,241,0.7)',
                  boxShadow: isActive ? '0 0 0 2px rgba(220,38,38,0.3)' : undefined,
                  cursor: 'crosshair',
                }}
              />
            );
          })}
        </div>

        <h3 style={{ margin: '16px 0 8px' }}>Typeset (engine output)</h3>
        <TypesetPreview
          text={text}
          width={width}
          lineBoxes={lineBoxes}
          graphemeMap={graphemeMap}
          runId="r0"
          fontFamily={fontFamily}
          fontSizePx={Math.round(fontSizePt * (96 / 72))}
          fontWeight={fontWeight}
        />

        <h3 style={{ margin: '16px 0 8px' }}>LineBoxes</h3>
        <div style={{ overflow: 'auto', maxHeight: 240, border: '1px solid #e5e7eb' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 4 }}>#</th>
                <th style={{ textAlign: 'left', padding: 4 }}>x</th>
                <th style={{ textAlign: 'left', padding: 4 }}>y</th>
                <th style={{ textAlign: 'left', padding: 4 }}>w</th>
                <th style={{ textAlign: 'left', padding: 4 }}>h</th>
                <th style={{ textAlign: 'left', padding: 4 }}>baseline</th>
                <th style={{ textAlign: 'left', padding: 4 }}>startG</th>
                <th style={{ textAlign: 'left', padding: 4 }}>endG</th>
              </tr>
            </thead>
            <tbody>
              {lineBoxes.map((b, i) => (
                <tr key={`row-${i}`}>
                  <td style={{ padding: 4 }}>{i}</td>
                  <td style={{ padding: 4 }}>{Math.round(b.x)}</td>
                  <td style={{ padding: 4 }}>{Math.round(b.y)}</td>
                  <td style={{ padding: 4 }}>{Math.round(b.width)}</td>
                  <td style={{ padding: 4 }}>{Math.round(b.height)}</td>
                  <td style={{ padding: 4 }}>{Math.round(b.baseline)}</td>
                  <td style={{ padding: 4 }}>{b.startGrapheme}</td>
                  <td style={{ padding: 4 }}>{b.endGrapheme}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 style={{ margin: '16px 0 8px' }}>Graphemes</h3>
        <div style={{ overflow: 'auto', maxHeight: 240, border: '1px solid #e5e7eb' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 4 }}>Key (runId:g)</th>
                <th style={{ textAlign: 'left', padding: 4 }}>line</th>
                <th style={{ textAlign: 'left', padding: 4 }}>x</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(graphemeMap).map(([k, v]) => (
                <tr key={`g-${k}`} onMouseEnter={() => setHoverKey(k)} onMouseLeave={() => setHoverKey(null)}>
                  <td style={{ padding: 4 }}>{k}</td>
                  <td style={{ padding: 4 }}>{v.lineIndex}</td>
                  <td style={{ padding: 4 }}>{Math.round(v.x)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default ParagraphInspector;


